import { UcoastEl } from '@/scripts/core/UcoastEl'
import { ATTRIBUTES } from '@/scripts/core/global'
import { initializeScrollAnimationTrigger } from '@/scripts/theme/animations'

export class InfiniteScroll extends UcoastEl {
	static htmlSelector = 'infinite-scroll'
	
	private loadMoreButton: HTMLButtonElement | null = null
	private productGrid: HTMLElement | null = null
	private currentPage: number = 1
	private totalPages: number = 1
	private sectionId: string = ''
	private isLoading: boolean = false

	constructor() {
		super()
		this.init()
	}

	private init() {
		// Get the load more button
		this.loadMoreButton = this.querySelector('button[data-load-more]')
		
		// Get product grid
		this.productGrid = document.querySelector('#product-grid')
		
		if (!this.loadMoreButton || !this.productGrid) {
			console.error('InfiniteScroll: Required elements not found')
			return
		}

		// Get initial data from attributes
		this.currentPage = parseInt(this.getAttribute('data-current-page') || '1')
		this.totalPages = parseInt(this.getAttribute('data-total-pages') || '1')
		this.sectionId = this.getAttribute('data-section-id') || ''

		// Bind event listeners
		this.loadMoreButton.addEventListener('click', this.loadMore.bind(this))

		// Set up Intersection Observer for automatic loading
		this.setupIntersectionObserver()

		// Update button visibility
		this.updateButtonVisibility()
	}

	private setupIntersectionObserver() {
		if (!this.loadMoreButton) return

		// Create an Intersection Observer to watch when the button becomes visible
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// If the button is visible and we're not already loading
					if (entry.isIntersecting && !this.isLoading && this.currentPage < this.totalPages) {
						this.loadMore()
					}
				})
			},
			{
				// Trigger when 50% of the button is visible
				threshold: 0.5,
				// Add some margin to trigger slightly before the button is fully visible
				rootMargin: '100px'
			}
		)

		// Start observing the load more button
		observer.observe(this.loadMoreButton)
	}

	private async loadMore() {
		if (this.isLoading || this.currentPage >= this.totalPages) {
			return
		}

		this.isLoading = true
		this.setLoadingState(true)

		try {
			const nextPage = this.currentPage + 1
			const url = this.buildUrl(nextPage)
			
			const response = await fetch(url)
			const html = await response.text()
			
			this.appendProducts(html)
			this.currentPage = nextPage
			this.updateButtonVisibility()
			
			// Initialize scroll animations for new products
			const newDocument = new DOMParser().parseFromString(html, 'text/html')
			if (typeof initializeScrollAnimationTrigger === 'function') {
				initializeScrollAnimationTrigger(newDocument.documentElement)
			}
		} catch (error) {
			console.error('InfiniteScroll: Error loading more products', error)
		} finally {
			this.isLoading = false
			this.setLoadingState(false)
		}
	}

	private buildUrl(page: number): string {
		const searchParams = new URLSearchParams(window.location.search)
		searchParams.set('page', page.toString())
		searchParams.set('section_id', this.sectionId)
		
		return `${window.location.pathname}?${searchParams.toString()}`
	}

	private appendProducts(html: string) {
		if (!this.productGrid) return

		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')
		const newProductGrid = doc.querySelector('#product-grid')
		
		if (!newProductGrid) {
			console.error('InfiniteScroll: Product grid not found in response')
			return
		}

		// Get all product items (li elements with class grid__item)
		const newProducts = newProductGrid.querySelectorAll('li.grid__item')
		
		if (newProducts.length === 0) {
			console.warn('InfiniteScroll: No products found in response')
			return
		}

		// Append each product to the existing grid
		newProducts.forEach((product) => {
			this.productGrid?.appendChild(product.cloneNode(true))
		})
	}

	private setLoadingState(loading: boolean) {
		if (!this.loadMoreButton) return

		if (loading) {
			this.loadMoreButton.setAttribute('disabled', 'disabled')
			this.loadMoreButton.classList.add('loading')
		} else {
			this.loadMoreButton.removeAttribute('disabled')
			this.loadMoreButton.classList.remove('loading')
		}
	}

	private updateButtonVisibility() {
		if (!this.loadMoreButton) return

		if (this.currentPage >= this.totalPages) {
			this.loadMoreButton.style.display = 'none'
		} else {
			this.loadMoreButton.style.display = ''
		}
	}

	// Public method to reset infinite scroll (called when filters/sorting change)
	public reset(currentPage: number = 1, totalPages: number = 1) {
		this.currentPage = currentPage
		this.totalPages = totalPages
		this.isLoading = false
		this.setLoadingState(false)
		this.updateButtonVisibility()
	}

	// Update from new HTML (called after filter/sort)
	public updateFromHTML(html: string) {
		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')
		const newInfiniteScroll = doc.querySelector('infinite-scroll')
		
		if (newInfiniteScroll) {
			const newCurrentPage = parseInt(newInfiniteScroll.getAttribute('data-current-page') || '1')
			const newTotalPages = parseInt(newInfiniteScroll.getAttribute('data-total-pages') || '1')
			this.reset(newCurrentPage, newTotalPages)
		} else {
			// No infinite scroll in new HTML, hide button
			this.reset(1, 1)
		}
	}
}
